import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const CERAMIC_IMG = "/images/warm-conversation.jpg";


const modalities = [
  { name: "CBT", desc: "Cognitive Behavioral Therapy — the interplay of thoughts, mood and behavior." },
  { name: "DBT", desc: "Mindfulness, emotion regulation, distress tolerance and interpersonal effectiveness." },
  { name: "ACT", desc: "Acceptance of what can't be changed, aligned with what you value most." },
  { name: "EMDR", desc: "Trauma healing without needing to verbally relive painful events." },
  { name: "EFT", desc: "Emotionally focused work that deepens connection to self and others." },
  { name: "Gottman Method", desc: "Research-backed couples therapy that rebuilds understanding, respect and connection." },
  { name: "ERP", desc: "Evidence-based treatment for OCD and anxiety-driven compulsions." },
  { name: "TRM", desc: "Re-regulating the nervous system after trauma, through the body." },
  { name: "Seeking Safety", desc: "Coping-first treatment for trauma and substance-use struggles." },
  { name: "Problem Solving Therapy", desc: "A structured path from problems to practical action." },
];
const principles = [
  {
    n: "01",
    title: "Understanding before technique",
    text: "Therapy is not about deciding who is right and who is wrong. It is about understanding what keeps happening, why it keeps happening, and what each person can begin doing differently.",
  },
  {
    n: "02",
    title: "Evidence-informed, never one-size-fits-all",
    text: "I tailor clinical approaches to the people in front of me — drawing on cognitive and behavioral strategies, trauma-informed work, solution-focused interventions, motivational interviewing and somatic techniques.",
  },
  {
    n: "03",
    title: "Emotionally safe and genuinely useful",
    text: "We will work toward insight, but we will also work toward practical change. You should leave sessions with both understanding and direction.",
  },
  {
    n: "04",
    title: "Honest, warm and nonjudgmental",
    text: "You can be honest about what is happening without feeling shamed or blamed. Directness and compassion are not opposites — in good therapy, they work together.",
  },
];

const Approach = () => {
  usePageMeta(
    "My Approach — Compassionate, Honest, Practical Therapy | Holistic Healing Psychotherapy",
    "Trauma-informed, evidence-informed therapy that is emotionally safe and practically useful. Learn what working together feels like."
  );

  return (
    <div data-testid="approach-page">
      <section className="bg-cream-50 pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeIn>
              <p className="eyebrow mb-6" data-testid="approach-eyebrow">My Approach</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink" data-testid="approach-headline">
                Therapy that is compassionate, honest and{" "}
                <em className="text-mauve-500">practical</em>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-xl" data-testid="approach-subcopy">
                Not a textbook explanation of modalities — a clear sense of what working together actually feels like,
                and how we move from insight to change.
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={0.15} className="lg:col-span-5 editorial-frame">
            <img
              src={CERAMIC_IMG}
              alt="A warm, honest conversation over coffee at the table"
              className="relative z-10 w-full h-[320px] sm:h-[420px] object-cover"
              data-testid="approach-hero-image"
            />
          </FadeIn>
        </div>
      </section>

      <section className="bg-forest-900 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading dark eyebrow="The Working Principles" title="How therapy with me unfolds." />
          <div className="mt-14 grid md:grid-cols-2 gap-x-16">
            {principles.map((p, i) => (
              <FadeIn key={p.n} delay={i * 0.08}>
                <div className="group py-8 border-t border-cream-50/10 hover:border-gold-500/40 transition-colors duration-500" data-testid={`approach-principle-${p.n}`}>
                  <div className="flex gap-6">
                    <span className="mt-3 w-3 h-3 rotate-45 bg-gold-500/70 group-hover:bg-gold-400 transition-colors duration-500 shrink-0" aria-hidden="true" />
                    <div>
                      <h3 className="font-serif text-2xl text-cream-50 mb-3">{p.title}</h3>
                      <p className="text-base text-cream-50/70 leading-relaxed">{p.text}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-100 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Clinical Toolkit" title="Evidence-based approaches, chosen for you." />
          <FadeIn delay={0.1}>
            <p className="mt-4 text-base text-sage leading-relaxed max-w-2xl" data-testid="approach-modalities-intro">
              I am an action-oriented therapist who leads sessions with focus, care and compassion. Depending on your
              needs, our work may draw on:
            </p>
          </FadeIn>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="approach-modalities-grid">
            {modalities.map((m, i) => (
              <FadeIn key={m.name} delay={i * 0.05}>
                <div className="h-full rounded-xl border border-forest-800/10 bg-cream-50 p-6 transition-colors duration-300 hover:border-gold-500/50" data-testid={`approach-modality-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <p className="font-serif text-xl text-forest-800 mb-2">{m.name}</p>
                  <p className="text-sm text-sage leading-relaxed">{m.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-forest-900 py-24 lg:py-32">
        <img
          src="/images/water-ripples.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-30 scale-105"
          data-testid="approach-ripple-bg"
        />
        <div className="absolute inset-0 bg-forest-950/40" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <FadeIn>
            <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-cream-50 leading-snug" data-testid="approach-quote">
              "We will work toward insight — but we will also work toward practical change."
            </p>
            <Link
              to="/contact"
              data-testid="approach-schedule-cta"
              className="inline-flex items-center gap-2 mt-10 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
            >
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <CTASection
        title="Curious whether this approach fits you?"
        copy="A short consultation is the easiest way to find out."
      />
    </div>
  );
};

export default Approach;
